import { useParams, Link } from 'react-router-dom';
import { getServiceBySlug, servicesData } from '../data/servicesData';
import SEO from '../components/SEO';
import { reportConversion } from '../utils/gtag';
import { useTranslation } from 'react-i18next';

export default function ServiceDetail() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) {
    return (
      <div className="pt-32 pb-24 max-w-3xl mx-auto px-4">
        <h1 className="text-3xl font-bold mb-4">Service Not Found</h1>
        <p className="text-slate-600 mb-6">The service you are looking for does not exist or has been renamed.</p>
        <Link to="/services" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition">
          Back to Services
        </Link>
      </div>
    );
  }

  const whatsappHref = `https://wa.me/971524333690?text=${encodeURIComponent(service.ctaWhatsAppText)}`;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.title,
    provider: {
      '@type': 'AutoRepair',
      name: 'Vigor Auto Repair Garage - Ras Al Khor Dubai',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Ras Al Khor Industrial Area',
        addressLocality: 'Ras Al Khor',
        addressRegion: 'Dubai',
        addressCountry: 'AE'
      }
    },
    areaServed: [
      { '@type': 'City', name: 'Dubai' },
      { '@type': 'Place', name: 'Ras Al Khor' },
      { '@type': 'Place', name: 'Nad Al Hamar' },
      { '@type': 'Place', name: 'Al Awir' },
      { '@type': 'Place', name: 'International City' }
    ],
    description: service.tagline,
  };

  return (
    <>
      <SEO
        title={`${service.title} Ras Al Khor Dubai - Vigor Auto Car Garage`}
        description={`Expert ${service.title.toLowerCase()} in Ras Al Khor Dubai. ${service.tagline} Dealer-alternative car garage serving Nad Al Hamar, Al Awir & International City. Free pickup/dropoff available.`}
        keywords={`${slug.replace(/-/g, ' ')} Ras Al Khor, ${slug.replace(/-/g, ' ')} dubai, ${service.title.toLowerCase()} Ras Al Khor, car garage Ras Al Khor, auto repair dubai, car service centre Ras Al Khor`}
        schema={serviceSchema}
      />
  <header className="pt-20 md:pt-28 pb-12 md:pb-16 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_75%_25%,#F5D313,transparent_60%)]" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <p className="uppercase tracking-widest text-xs font-semibold text-[#F5D313]/80">Service</p>
          <h1 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">{service.title}</h1>
          <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl">{service.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer" onClick={() => reportConversion('https://wa.me/971524333690')} className="inline-flex rounded-lg bg-[#F5D313] px-6 py-3 text-sm font-semibold text-slate-900 shadow hover:bg-[#e4c40f] transition">
              {t('nav.chatWhatsApp')}
            </a>
            <a href="tel:+971568699800" onClick={() => reportConversion('tel:+971568699800')} className="inline-flex rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">
              {t('common.callUs')}
            </a>
            <Link to="/request-quote" className="inline-flex rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">
              Request Quote
            </Link>
            <Link to="/services" className="inline-flex rounded-lg border border-white/20 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">
              All Services
            </Link>
          </div>
        </div>
      </header>

  <section className="py-10 md:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <h2 className="text-2xl font-bold tracking-tight mb-4">Overview</h2>
                <p className="text-slate-700 leading-relaxed mb-8 text-sm md:text-base">{service.summary}</p>

                <h3 className="text-xl font-semibold mb-3 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#F5D313]"></span>What You Get</h3>
                <ul className="grid sm:grid-cols-2 gap-2 mb-10 text-sm text-slate-700">
                  {service.inclusions.map(item => (
                    <li key={item} className="flex gap-2 items-start">
                      <span className="text-[#F5D313] mt-1">•</span><span>{item}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-semibold mb-3 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#F5D313]"></span>Ideal Benefits</h3>
                <ul className="mb-10 space-y-2 text-sm text-slate-700">
                  {service.benefits.map(b => (
                    <li key={b} className="flex gap-2"><span className="text-[#F5D313]">•</span><span>{b}</span></li>
                  ))}
                </ul>

                <h3 className="text-xl font-semibold mb-3 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#F5D313]"></span>Common Symptoms</h3>
                <ul className="mb-10 space-y-2 text-sm text-slate-700">
                  {service.symptoms.map(s => (
                    <li key={s} className="flex gap-2"><span className="text-[#F5D313]">•</span><span>{s}</span></li>
                  ))}
                </ul>

                <div className="p-6 rounded-xl bg-slate-50 border border-slate-200">
                  <h3 className="font-semibold mb-2 text-slate-800">Not Sure If You Need This?</h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">Send us a quick voice note or photo on WhatsApp—our team can usually triage the next best step in minutes.</p>
                  <a href={whatsappHref} target="_blank" rel="noopener noreferrer" onClick={() => reportConversion('https://wa.me/971524333690')} className="inline-flex items-center gap-2 rounded-lg bg-[#F5D313] px-5 py-2.5 text-xs font-semibold text-slate-900 shadow hover:bg-[#e4c40f] transition">
                    Quick WhatsApp Triage
                  </a>
                </div>
              </div>
            </div>

            <aside className="space-y-8">
              <div className="p-6 rounded-2xl bg-blue-950 text-white relative overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_40%,#F5D313,transparent_70%)]" />
                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-[#F5D313]">Fast Access</h3>
                <ul className="space-y-2 text-sm text-slate-200">
                  <li className="flex justify-between"><span>WhatsApp Response</span><span className="text-[#F5D313] font-medium">~5 min</span></li>
                  <li className="flex justify-between"><span>Standard Diagnostic</span><span className="text-[#F5D313] font-medium">Same Day</span></li>
                  <li className="flex justify-between"><span>Priority Booking</span><span className="text-[#F5D313] font-medium">Available</span></li>
                </ul>
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="relative z-10 mt-6 inline-flex w-full justify-center rounded-lg bg-[#F5D313] px-5 py-2.5 text-sm font-semibold text-slate-900 shadow hover:bg-[#e4c40f] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#F5D313]">Chat Now</a>
              </div>

              <div className="p-6 rounded-2xl bg-white border border-slate-200">
                <h3 className="font-semibold mb-3 text-slate-800 text-sm uppercase tracking-wide">All Services</h3>
                <ul className="space-y-2 text-sm">
                  {servicesData.map(s => (
                    <li key={s.slug}>
                      <Link to={`/services/${s.slug}`} className={`block px-3 py-2 rounded-lg border text-slate-700 hover:border-[#F5D313] hover:bg-[#F5D313]/5 transition ${s.slug === slug ? 'border-[#F5D313] bg-[#F5D313]/10' : 'border-slate-200'}`}>{s.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200">
                <h3 className="font-semibold mb-3 text-slate-800 text-sm uppercase tracking-wide">Need Something Else?</h3>
                <p className="text-slate-600 text-xs leading-relaxed mb-4">If your requirement looks atypical—retrofit, modification advisory, pre-purchase forensic review—reach out and we can scope it.</p>
                <Link to="/contact" className="inline-flex rounded-lg bg-slate-900 px-4 py-2.5 text-xs font-semibold text-white hover:bg-slate-700 transition">Contact Form</Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
